/**
 * Refined Design — swapping one render for another.
 *
 * Every colour picker on the site changes a photograph, and the plain swap — assign
 * a new src and wait — reads badly. An <img> keeps drawing what it has until the
 * replacement is decoded, so the old finish stays sharp for the whole fetch, nothing
 * says the click landed, and then the picture cuts to the new colour in one frame.
 *
 * So the swap is staged instead. The picture softens the moment it is asked for,
 * further the longer it is kept waiting, and sharpens back once the replacement is
 * decoded and in place — the shopper sees the vase let go of one colour and settle
 * into the next. The softening is held off for a beat by the stylesheet, so a render
 * already in the browser's cache is swapped before any blur is painted and an
 * instant change still looks instant. Where motion is unwelcome the same steps run
 * as a plain dip in opacity.
 *
 * Only the element already on screen is ever used — never a fresh one — because a
 * fresh <img> has nothing to keep drawing and would blink through empty.
 *
 *   RdPhotoSwap.swap(img, { src, srcset, sizes, alt, width, height })
 *   RdPhotoSwap.preload({ src, srcset, sizes })
 */
(function () {
  const SWAPPING = 'is-swapping';
  const MARK = 'rd-swap';

  /** The swap each <img> is currently waiting on, so a later pick can drop an earlier one. */
  const waiting = new WeakMap();

  /** Renders already sent for, so asking twice costs nothing. */
  const warmed = new Set();

  /** An off-screen request for a render, which is what puts it in the browser's cache. */
  function fetchPhoto(next) {
    const probe = new Image();
    if (next.sizes) probe.sizes = next.sizes;
    if (next.srcset) probe.srcset = next.srcset;
    probe.src = next.src;
    return probe;
  }

  /**
   * Warms a render the shopper has not asked for yet but plausibly will — the other
   * finishes of the card they just reached, say — so that the swap, when it comes, is
   * a decode rather than a download. Called as often as the caller likes.
   */
  function preload(next) {
    if (!next || !next.src || warmed.has(next.src)) return null;
    warmed.add(next.src);
    return fetchPhoto(next);
  }

  /**
   * Shows `next` in `img`, once it can be shown.
   *
   * Resolves when the picture is in place — or when it has failed, since a render
   * that never arrives must not leave the element soft forever.
   */
  function swap(img, next) {
    if (!img || !next || !next.src) return Promise.resolve();

    if (img.getAttribute('src') === next.src) {
      // Already the right colour, so there is nothing to wait for. Any swap still in
      // flight was asked for a colour the shopper has since moved off — a swatch tried
      // on and left — so it is dropped: without this it would land after the pointer
      // had gone and put a finish back that was let go of. Losing its token leaves it
      // to settle into nothing, and the picture is sharp again now rather than then.
      if (waiting.has(img)) {
        waiting.delete(img);
        img.classList.remove(SWAPPING);
      }
      dress(img, next);
      return Promise.resolve();
    }

    const token = {};
    waiting.set(img, token);
    img.classList.add(MARK, SWAPPING);

    warmed.add(next.src);
    const probe = fetchPhoto({ src: next.src, srcset: next.srcset, sizes: next.sizes || img.sizes });

    const settle = () => {
      if (waiting.get(img) !== token) return; // a later pick has taken this element over
      dress(img, next);
      if (next.srcset) img.setAttribute('srcset', next.srcset);
      else img.removeAttribute('srcset'); // a server srcset would otherwise outvote the new src
      img.setAttribute('src', next.src);
      // Two frames: one for the new picture to be taken up, one for it to be painted.
      // Lifting the blur any earlier sharpens the colour being left behind and then
      // cuts to the new one anyway, which is the swap this file exists to avoid.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (waiting.get(img) !== token) return;
          img.classList.remove(SWAPPING);
          waiting.delete(img);
        });
      });
    };

    return (probe.decode ? probe.decode() : Promise.resolve()).then(settle, settle);
  }

  /** The parts of a swap that are not the picture itself. */
  function dress(img, next) {
    if (next.alt != null) img.setAttribute('alt', next.alt);
    // Its own shape, so a render framed differently from the one before it takes the
    // room it needs before it lands rather than shunting the page on arrival.
    if (next.width > 0 && next.height > 0) {
      img.setAttribute('width', String(next.width));
      img.setAttribute('height', String(next.height));
    }
  }

  window.RdPhotoSwap = { swap, preload };
})();
